/**
 * UI surface a product interaction originated from. Reported as the `source`
 * property of cart analytics events, so the values are a BI-facing contract —
 * renaming one breaks existing reports.
 */
export type ProductSourceType =
  | 'product_page'
  | 'home_carousel'
  | 'home_grid'
  | 'category_page'
  | 'search'
  | 'favorites'
  | 'recommendations'
  | 'cart'
  | 'reorder';

/**
 * Surface attribution for a cart mutation. Emitted as `source`, `source_id`
 * and `source_name` on the resulting analytics events.
 */
export interface AddToCartSource {
  /** Type of the surface the interaction started from */
  type: ProductSourceType;
  /** Identifies the specific element, e.g. the CMS category id of a carousel */
  id?: string;
  /** Name of the specific element, e.g. "Bundle Deals" */
  name?: string;
}

/**
 * Attribution for surfaces that render a single product rather than a list:
 * the product page itself and the modals opened from it.
 */
export const PRODUCT_PAGE_SOURCE: AddToCartSource = { type: 'product_page' };

/** Quantity change made on an item already in the cart */
export const CART_SOURCE: AddToCartSource = { type: 'cart' };

/** Item added back from a past order */
export const REORDER_SOURCE: AddToCartSource = { type: 'reorder' };

/**
 * Search suggest in the header. Carries its own id so BI can tell it from the
 * search results page, which reports the same `search` type.
 */
export const SEARCH_SUGGEST_SOURCE: AddToCartSource = {
  type: 'search',
  id: 'search_suggest',
  name: 'Search suggest',
};
